import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from 'shared/components/ui/ModalShell';
import { buttonClasses } from 'styles/tailwindClassnames';

// What a screen hands over when the user tries to leave a section with unsaved
// edits. onSave resolves false when the save didn't land (validation, API
// failure), in which case the move is abandoned and the user stays put with the
// errors on screen.
export type SectionPrompt = {
  onSave: () => Promise<boolean | void> | boolean | void;
  onDiscard: () => void;
  proceed: () => void;
};

// How a panel that owns unsaved edits lets the screen around it ask the same
// question. The tabs above a configuration panel, and the routes out of the page,
// live outside the panel but can lose its edits just as easily — they consult
// this instead of navigating blind. The panel keeps the ref current; holders read
// it at click time, never during render.
export type PendingEdits = {
  hasEdits: () => boolean;
  save: () => Promise<boolean>;
  discard: () => void;
};

// Every configuration screen saves one section at a time, so every one of them
// has to ask this same question when the user moves on. Shared so the wording and
// the behaviour can't drift apart between the flow map and the Connection screen.
const UnsavedSectionPrompt = ({
  prompt,
  onClose,
}: {
  prompt: SectionPrompt | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  if (!prompt) return null;

  return (
    <ModalShell
      title={t('Save changes?')}
      // Closing mid-save would only look like it cancelled: the in-flight
      // handler still owns the answer and would move the user on when the
      // request lands. The footer buttons are disabled for the same reason.
      onClose={() => {
        if (!saving) onClose();
      }}
      footer={
        <>
          <button
            type="button"
            className={buttonClasses.primaryAction}
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const ok = await prompt.onSave();
                onClose();
                if (ok !== false) prompt.proceed();
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('Save')}
          </button>
          {/* No Cancel next to it: the two answers are save or discard, and
                    the dialog's ✕ already means "never mind". */}
          <button
            type="button"
            className={buttonClasses.destructiveAction}
            disabled={saving}
            onClick={() => {
              prompt.onDiscard();
              onClose();
              prompt.proceed();
            }}
          >
            {t('Discard changes')}
          </button>
        </>
      }
    >
      <p className="text-body-md text-ui-text-primary-text">
        {t(
          'This section has unsaved changes. Save them before moving on, or discard them.',
        )}
      </p>
    </ModalShell>
  );
};

export default UnsavedSectionPrompt;
